import { Injectable } from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, catchError, forkJoin, Observable, of, tap, throwError, zip} from 'rxjs';
import {KlasifikatoriusApi} from "../api/klasifikatorius.api";
import {environment} from "../../environments/environment";
import {TabakoKlasifikatoriusApi} from "../api/tabako-klasifikatorius.api";
import {KlasifikatoriusService} from "./klasifikatorius.service";
import {SharedDataService} from "./shared-data.service";
import {AppNotificationService} from "./app-notification.service";
import {AppInitializer} from "./app-initializer.service";

const CREATE_ACTION = "createKlasifikatorius";
const UPDATE_ACTION = "updateKlasifikatorius";
const DELETE_ACTION = "deleteKlasifikatorius";

const itemIndex = (item: TabakoKlasifikatoriusApi, data: TabakoKlasifikatoriusApi[]): number => {
  for (let idx = 0; idx < data.length; idx++) {
    if (data[idx].id === item.id) {
      return idx;
    }
  }

  return -1;
};

const cloneData = (data: TabakoKlasifikatoriusApi[]) =>
  data.map((item) => Object.assign({}, item));

@Injectable({
  providedIn: 'root'
})
export class KlasifikatoriusEditService extends BehaviorSubject<TabakoKlasifikatoriusApi[]> {
  public loading!: boolean;
  private data: TabakoKlasifikatoriusApi[] = [];
  private originalData: TabakoKlasifikatoriusApi[] = [];
  private createdItems: TabakoKlasifikatoriusApi[] = [];
  private updatedItems: TabakoKlasifikatoriusApi[] = [];
  private deletedItems: TabakoKlasifikatoriusApi[] = [];

  constructor(private http: HttpClient,
              private klasifikatoriusService: KlasifikatoriusService,
              private sharedDataService: SharedDataService,
              private appNofiticationService: AppNotificationService,
              private initializerService: AppInitializer) {
    super([]);
  }

  getKlasifikatoriausData(): Observable<TabakoKlasifikatoriusApi[]> {
    return this.asObservable();
  }

  public read(): void {
    if (this.data.length) {
      return super.next(this.data);
    }
    this.loading = true;
    this.klasifikatoriusService.getTabakoKlasifikatorius().subscribe((data) => {
      this.data = data;
      this.originalData = cloneData(data);
      super.next(data);
    },(error) => {
      console.log(error);
    }, () => {
      this.loading = false;
    });
  }

  public isNew(item: TabakoKlasifikatoriusApi): boolean {
    return !item.id;
  }

  public hasChanges(): boolean {
    return Boolean(
      this.deletedItems.length ||
      this.updatedItems.length ||
      this.createdItems.length
    );
  }

  public create(item: TabakoKlasifikatoriusApi): void {
    this.createdItems.push(item);
    this.data.unshift(item);

    super.next(this.data);
  }

  public update(item: TabakoKlasifikatoriusApi): void {
    if (!this.isNew(item)) {
      const index = itemIndex(item, this.updatedItems);
      if (index !== -1) {
        this.updatedItems.splice(index, 1, item);
      } else {
        this.updatedItems.push(item);
      }
    } else {
      const index = this.createdItems.indexOf(item);
      this.createdItems.splice(index, 1, item);
    }
  }

  public remove(item: TabakoKlasifikatoriusApi): void {
    let index = itemIndex(item, this.data);
    this.data.splice(index, 1);

    index = itemIndex(item, this.createdItems);
    if (index >= 0) {
      this.createdItems.splice(index, 1);
    } else {
      this.deletedItems.push(item);
    }

    index = itemIndex(item, this.updatedItems);
    if (index >= 0) {
      this.updatedItems.splice(index, 1);
    }

    super.next(this.data);
  }

  public assignValues(target: TabakoKlasifikatoriusApi, source: TabakoKlasifikatoriusApi): void {
    Object.assign(target, source);
  }

  public cancelChanges(): void {
    this.reset();

    this.data = this.originalData;
    this.originalData = cloneData(this.originalData);
    super.next(this.data);
  }

  private reset() {
    this.data = [];
    this.deletedItems = [];
    this.updatedItems = [];
    this.createdItems = [];
  }

  public saveChanges(): void {
    if (!this.hasChanges()) {
      return;
    }

    const completed = [];
    if (this.deletedItems.length) {
      completed.push(this.fetch(DELETE_ACTION, this.deletedItems));
    }

    if (this.updatedItems.length) {
      completed.push(this.fetch(UPDATE_ACTION, this.updatedItems));
    }

    if (this.createdItems.length) {
      completed.push(this.fetch(CREATE_ACTION, this.createdItems));
    }

    this.reset();

    forkJoin(completed).subscribe(
      (results: any) => {
        const completed = results.some((result: any) => result !== null);
        if (completed) {
          this.appNofiticationService.showNotification('success', 'notification.duomenys.issaugoti');
        }
        this.reset();
        this.read();
        this.initializerService.initializeKlasifikatorius();
      },
      (error) => {
        this.sharedDataService.setKlaidos(error.error);
        this.appNofiticationService.showNotification('error', error.error);
      }
    );
  }

  fetch(action: string, items: any[]): Observable<any> {
    const requestUrl = `${environment.apiBaseUrl}/internalApi/${action}`;

    return this.http.post<any>(requestUrl, items, { withCredentials: true }).pipe(
      catchError((error) => {
        this.appNofiticationService.showNotification('error', error.error);
        return of(null);
      })
    );
  }
}
